import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

@Component({
  selector: 'app-custom-date-range-picker',
  templateUrl: './custom-date-range-picker.component.html',
  styleUrls: ['./custom-date-range-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDateRangePickerComponent),
      multi: true
    }
  ]
})
export class CustomDateRangePickerComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Select Date Range';
  @Input() disabled: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() startDatePlaceholder: string = 'Start Date';
  @Input() endDatePlaceholder: string = 'End Date';

  @Output() dateRangeChange = new EventEmitter<DateRange>();

  isOpen = false;
  selectedRange: DateRange = { startDate: null, endDate: null };
  hoverDate: Date | null = null; // Track hover date for preview
  currentMonth: Date = new Date();
  calendarDays: Date[] = [];
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];

  isMonthYearView = false;
  isYearView = false;
  yearRange: number[] = [];
  currentYearRange = 0;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.generateCalendar();
    this.generateYearRange();
  }

  writeValue(value: DateRange | null): void {
    if (value) {
      this.selectedRange = { ...value };
    } else {
      this.selectedRange = { startDate: null, endDate: null };
    }
    if (value?.startDate) {
      this.currentMonth = new Date(value.startDate);
      this.generateCalendar();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleCalendar(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.closeAllViews();

    if (this.isOpen) {
      if (this.selectedRange.startDate) {
        this.currentMonth = new Date(this.selectedRange.startDate);
      } else if (this.selectedRange.endDate) {
        this.currentMonth = new Date(this.selectedRange.endDate);
      }
      this.generateCalendar();
    }

    this.onTouched();
  }

  closeCalendar(): void {
    this.isOpen = false;
    this.closeAllViews();
  }

  selectDate(date: Date): void {
    if (!this.selectedRange.startDate || (this.selectedRange.startDate && this.selectedRange.endDate)) {
      // Start new range
      this.selectedRange = { startDate: date, endDate: null };
    } else {
      // Complete the range
      if (date >= this.selectedRange.startDate!) {
        this.selectedRange.endDate = date;
        this.onChange(this.selectedRange);
        this.dateRangeChange.emit(this.selectedRange);
        this.closeCalendar();
      } else {
        // If end date is before start date, swap them
        this.selectedRange = { startDate: date, endDate: this.selectedRange.startDate };
        this.onChange(this.selectedRange);
        this.dateRangeChange.emit(this.selectedRange);
        this.closeCalendar();
      }
    }
  }

  clearRange(): void {
    this.selectedRange = { startDate: null, endDate: null };
    this.onChange(this.selectedRange);
    this.dateRangeChange.emit(this.selectedRange);
  }

  previousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.generateCalendar();
  }

  generateCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    this.calendarDays = [];
    for (let i = 0; i < 42; i++) {
      this.calendarDays.push(new Date(startDate));
      startDate.setDate(startDate.getDate() + 1);
    }
  }

  isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentMonth.getMonth();
  }

  isSelected(date: Date): boolean {
    return this.selectedRange.startDate?.toDateString() === date.toDateString() ||
           this.selectedRange.endDate?.toDateString() === date.toDateString();
  }

  isInRange(date: Date): boolean {
    if (!this.selectedRange.startDate || !this.selectedRange.endDate) return false;
    return date >= this.selectedRange.startDate && date <= this.selectedRange.endDate;
  }

  isToday(date: Date): boolean {
    return date.toDateString() === new Date().toDateString();
  }

  isDisabled(date: Date): boolean {
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  getDisplayValue(): string {
    if (!this.selectedRange.startDate && !this.selectedRange.endDate) {
      return '';
    }
    
    if (this.selectedRange.startDate && !this.selectedRange.endDate) {
      return `${this.selectedRange.startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })} - Select End Date`;
    }
    
    if (this.selectedRange.startDate && this.selectedRange.endDate) {
      return `${this.selectedRange.startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })} - ${this.selectedRange.endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })}`;
    }
    
    return '';
  }

  getMonthYearDisplay(): string {
    return `${this.months[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  toggleMonthYearView(): void {
    this.isMonthYearView = !this.isMonthYearView;
    this.isYearView = false;
  }

  toggleYearView(): void {
    this.isYearView = !this.isYearView;
    this.isMonthYearView = false;
  }

  selectMonth(monthIndex: number): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), monthIndex, 1);
    this.generateCalendar();
    this.isMonthYearView = false;
  }

  selectYear(year: number): void {
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.generateCalendar();
    this.isYearView = false;
    if (this.isMonthYearView) {
      this.isMonthYearView = false;
    }
  }

  previousYearRange(): void {
    this.currentYearRange -= 12;
    this.generateYearRange();
  }

  nextYearRange(): void {
    this.currentYearRange += 12;
    this.generateYearRange();
  }

  generateYearRange(): void {
    const currentYear = this.currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12 + this.currentYearRange;
    this.yearRange = Array.from({ length: 12 }, (_, i) => startYear + i);
  }

  goToYearView(): void {
    this.isYearView = true;
    this.isMonthYearView = false;
    this.generateYearRange();
  }

  goToMonthView(): void {
    this.isMonthYearView = true;
    this.isYearView = false;
  }

  closeAllViews(): void {
    this.isMonthYearView = false;
    this.isYearView = false;
  }

  onDateMouseEnter(date: Date): void {
    // Only show hover preview when we have a start date but no end date
    if (this.selectedRange.startDate && !this.selectedRange.endDate && !this.isDisabled(date)) {
      this.hoverDate = date;
    }
  }

  onDateMouseLeave(): void {
    this.hoverDate = null;
  }

  isInHoverRange(date: Date): boolean {
    if (!this.hoverDate || !this.selectedRange.startDate || this.selectedRange.endDate) {
      return false;
    }
    
    const start = this.selectedRange.startDate;
    const hover = this.hoverDate;
    
    // Check if date is between start date and hover date
    if (start <= hover) {
      return date >= start && date <= hover;
    } else {
      return date >= hover && date <= start;
    }
  }
} 